import { useEffect, useRef, useState } from "react";
import { Camera, ImagePlus, X, Loader2, Images, AlertTriangle, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { imageSrc, uploadImage } from "@/lib/files";
import { cn } from "@/lib/utils";

// Downscale image client-side and return a data URI (JPEG). Used for AI type-plate reading only.
export async function fileToDataUrl(file, maxDim = 1600, quality = 0.82) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        let { width, height } = img;
        if (width > maxDim || height > maxDim) {
          if (width >= height) {
            height = Math.round((height * maxDim) / width);
            width = maxDim;
          } else {
            width = Math.round((width * maxDim) / height);
            height = maxDim;
          }
        }
        const canvas = document.createElement("canvas");
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext("2d");
        ctx.fillStyle = "#ffffff";
        ctx.fillRect(0, 0, width, height);
        ctx.drawImage(img, 0, 0, width, height);
        resolve(canvas.toDataURL("image/jpeg", quality));
      };
      img.onerror = reject;
      img.src = e.target.result;
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

let _localId = 0;
const nextLocalId = () => `local-${Date.now()}-${_localId++}`;

/**
 * Photo uploader – photos are uploaded to object storage immediately (with progress) and the
 * component keeps one item per photo:
 *   { key, file_id, caption, status: "uploading" | "done" | "error", progress, preview, file, width, height }
 * Use `toImageRefs(value)` from lib/files to build the API payload.
 */
export const PhotoUploader = ({ value = [], onChange, testidPrefix = "photo", max = 12, kind = "report", refId, showCaptions = true }) => {
  const cameraRef = useRef(null);
  const galleryRef = useRef(null);
  const valueRef = useRef(value);
  const [dragOver, setDragOver] = useState(false);
  useEffect(() => { valueRef.current = value; }, [value]);

  const patch = (key, changes) => {
    const next = valueRef.current.map((i) => (i.key === key ? { ...i, ...changes } : i));
    valueRef.current = next;
    onChange(next);
  };

  const startUpload = async (item) => {
    try {
      const rec = await uploadImage(item.file, {
        kind, refId, caption: item.caption,
        onProgress: (p) => patch(item.key, { progress: p }),
      });
      patch(item.key, { status: "done", progress: 100, file_id: rec.id, width: rec.width, height: rec.height, file: undefined });
    } catch (e) {
      const msg = e?.response?.data?.detail || "Opplasting feila.";
      patch(item.key, { status: "error", error: msg });
      toast.error(msg);
    }
  };

  const handleFiles = async (files) => {
    if (!files || files.length === 0) return;
    const room = Math.max(0, max - valueRef.current.length);
    const list = Array.from(files).filter((f) => f.type.startsWith("image/") || /\.(heic|heif)$/i.test(f.name)).slice(0, room);
    if (list.length === 0) {
      toast.error(room === 0 ? `Maks ${max} bilete.` : "Berre bilete kan lastast opp.");
      return;
    }
    const items = list.map((file) => ({
      key: nextLocalId(), file, preview: URL.createObjectURL(file), caption: "", status: "uploading", progress: 0,
    }));
    const next = [...valueRef.current, ...items];
    valueRef.current = next;
    onChange(next);
    items.forEach(startUpload);
    if (cameraRef.current) cameraRef.current.value = "";
    if (galleryRef.current) galleryRef.current.value = "";
  };

  const removeItem = (item) => {
    if (item.preview) URL.revokeObjectURL(item.preview);
    const next = valueRef.current.filter((i) => i.key !== item.key);
    valueRef.current = next;
    onChange(next);
  };

  const retry = (item) => {
    patch(item.key, { status: "uploading", progress: 0, error: undefined });
    startUpload({ ...item });
  };

  const count = value.length;
  const uploading = value.filter((i) => i.status === "uploading").length;

  return (
    <div
      className="space-y-3"
      data-testid={`${testidPrefix}-uploader`}
      onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
      onDragLeave={() => setDragOver(false)}
      onDrop={(e) => { e.preventDefault(); setDragOver(false); handleFiles(e.dataTransfer.files); }}
    >
      <input ref={cameraRef} data-testid={`${testidPrefix}-camera-input`} type="file" accept="image/*" capture="environment" className="hidden" onChange={(e) => handleFiles(e.target.files)} />
      <input ref={galleryRef} data-testid={`${testidPrefix}-upload-input`} type="file" accept="image/*,.heic,.heif" multiple className="hidden" onChange={(e) => handleFiles(e.target.files)} />

      <div className="grid grid-cols-2 gap-2 sm:flex sm:flex-wrap">
        <Button type="button" variant="dark" data-testid={`${testidPrefix}-take-photo-button`} onClick={() => cameraRef.current?.click()} disabled={count >= max} className="h-12">
          <Camera /> Ta bilete
        </Button>
        <Button type="button" variant="outline" data-testid={`${testidPrefix}-add-photo-button`} onClick={() => galleryRef.current?.click()} disabled={count >= max} className="h-12">
          <ImagePlus /> Vel frå galleri
        </Button>
      </div>

      {count === 0 ? (
        <div
          data-testid={`${testidPrefix}-empty`}
          className={cn(
            "flex items-center gap-3 rounded-lg border border-dashed px-4 py-4 text-sm text-muted-foreground transition-colors",
            dragOver ? "border-primary bg-primary/5" : "border-border bg-muted/40"
          )}
        >
          <Images className="h-5 w-5" />
          Ingen bilete lagt til enno. Bilete blir lasta opp med ein gong og lagt fint inn i PDF-rapporten.
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4" data-testid={`${testidPrefix}-grid`}>
          {value.map((item, idx) => {
            const src = item.preview || imageSrc(item, { size: "thumb" });
            return (
              <div key={item.key || item.file_id || idx} className="group overflow-hidden rounded-lg border border-border bg-card shadow-sm" data-testid={`${testidPrefix}-thumb-${idx}`}>
                <div className="relative aspect-[4/3] bg-muted">
                  {src && <img src={src} alt={item.caption || `Bilete ${idx + 1}`} className={cn("h-full w-full object-cover transition-opacity", item.status === "uploading" && "opacity-60")} />}
                  {item.status === "uploading" && (
                    <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 bg-black/35 text-white" data-testid={`${testidPrefix}-progress-${idx}`}>
                      <Loader2 className="h-6 w-6 animate-spin" />
                      <span className="text-xs font-semibold tabular-nums">{item.progress || 0}%</span>
                      <div className="absolute inset-x-0 bottom-0 h-1 bg-white/30"><div className="h-full bg-primary transition-[width] duration-150" style={{ width: `${item.progress || 0}%` }} /></div>
                    </div>
                  )}
                  {item.status === "error" && (
                    <div className="absolute inset-0 flex flex-col items-center justify-center gap-1.5 bg-black/60 p-2 text-center text-white" data-testid={`${testidPrefix}-error-${idx}`}>
                      <AlertTriangle className="h-5 w-5 text-primary" />
                      <span className="text-[11px] leading-tight">{item.error || "Opplasting feila"}</span>
                      <Button type="button" size="sm" variant="secondary" className="h-8" onClick={() => retry(item)} data-testid={`${testidPrefix}-retry-${idx}`}><RotateCcw /> Prøv igjen</Button>
                    </div>
                  )}
                  <button
                    type="button"
                    aria-label="Fjern bilete"
                    data-testid={`${testidPrefix}-remove-${idx}`}
                    onClick={() => removeItem(item)}
                    className="absolute right-1.5 top-1.5 flex h-8 w-8 items-center justify-center rounded-full bg-black/65 text-white transition-colors hover:bg-destructive"
                  >
                    <X className="h-4 w-4" />
                  </button>
                  <span className="absolute bottom-1.5 left-1.5 rounded bg-black/60 px-1.5 py-0.5 text-[10px] font-semibold text-white">{idx + 1}</span>
                </div>
                {showCaptions && (
                  <Input
                    value={item.caption || ""}
                    onChange={(e) => patch(item.key, { caption: e.target.value })}
                    placeholder="Bilettekst (valfri)"
                    maxLength={120}
                    className="h-9 rounded-none border-0 border-t bg-card text-xs shadow-none focus-visible:ring-0"
                    data-testid={`${testidPrefix}-caption-${idx}`}
                  />
                )}
              </div>
            );
          })}
        </div>
      )}
      <p className="text-xs text-muted-foreground" data-testid={`${testidPrefix}-count`}>
        {count} av {max} bilete{uploading > 0 ? ` · lastar opp ${uploading} …` : ""}
      </p>
    </div>
  );
};
